import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Typography,
  Chip,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import {
  tacticalAccordionSx,
  tacticalSummarySx,
  tacticalChipSx,
  tacticalActionSx,
} from "../../utils/fdcTheme";

const compactFieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: 1.25, height: 44 },
  "& .MuiInputLabel-root": { fontSize: 13, fontWeight: 700 },
  "& input, & .MuiSelect-select": { fontWeight: 700, fontVariantNumeric: "tabular-nums" },
};

export default function MissionDetailsPanel({
  expanded,
  onChange,
  hasMission,
  form,
  handleChange,
  setForm,
  ptlError,
  validatePtl,
  mgrsError,
  handleMgrsConfirm,
  getChargeOptions,
  weaponOptions,
  onSave,
}) {
  const isPtlPending = !String(form.ptl ?? "").trim();
  const hasPtlError = Boolean(ptlError);

  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      disabled={!hasMission}
      disableGutters
      sx={(theme) => tacticalAccordionSx(theme)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={(theme) => tacticalSummarySx(theme)}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%", minWidth: 0 }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
              Mission
            </Typography>
            {form.mgrs_target && (
              <Chip
                size="small"
                label={form.mgrs_target}
                sx={(theme) => ({
                  ...tacticalChipSx(theme),
                  fontVariantNumeric: "tabular-nums",
                })}
              />
            )}
            {form.target_altitude > 0 && (
              <Chip
                size="small"
                label={`${form.target_altitude} m`}
                sx={(theme) => tacticalChipSx(theme)}
              />
            )}
            {isPtlPending && (
              <Chip size="small" label="PTL required" sx={(theme) => tacticalChipSx(theme, "warning")} />
            )}
          </Stack>
          <Typography
            variant="caption"
            noWrap
            sx={{
              color: "text.secondary",
              letterSpacing: 0.2,
              fontWeight: 700,
              maxWidth: { xs: "100%", sm: "60%" },
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {form.description || "Target reference, weapon, charge, PTL"}
          </Typography>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ px: { xs: 1.25, sm: 1.5 }, pb: 1.5, pt: 1 }}>
        <Grid container spacing={1}>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              label="MGRS Zone"
              name="mgrs_zone"
              value={form.mgrs_zone}
              onChange={handleChange}
              required
              size="small"
              sx={compactFieldSx}
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small" sx={compactFieldSx}>
              <InputLabel id="weapon-select-label">Weapon</InputLabel>
              <Select
                labelId="weapon-select-label"
                name="weapon"
                value={form.weapon}
                onChange={handleChange}
                label="Weapon"
              >
                {(weaponOptions && weaponOptions.length
                  ? weaponOptions
                  : [{ value: "M109", label: "M109" }, { value: "M114", label: "M114" }, { value: "M119", label: "M119" }]
                ).map((wo) => (
                  <MenuItem key={wo.value} value={wo.value}>{wo.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small" sx={compactFieldSx}>
              <InputLabel id="charge-select-label">Charge</InputLabel>
              <Select
                labelId="charge-select-label"
                name="charge"
                value={form.charge}
                onChange={handleChange}
                label="Charge"
              >
                {getChargeOptions(form.weapon).map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              required
              label="PTL (mils)"
              name="ptl"
              type="number"
              value={form.ptl}
              onChange={(e) => setForm((prev) => ({ ...prev, ptl: e.target.value }))}
              onBlur={validatePtl}
              size="small"
              inputProps={{ min: 0, max: 6400, step: 10 }}
              error={hasPtlError}
              helperText={ptlError || (isPtlPending ? "0–6400 mils (required)" : "0–6400 mils")}
              sx={{
                ...compactFieldSx,
                "& .MuiFormHelperText-root": {
                  fontSize: 11,
                  margin: "2px 0 0 0",
                  fontWeight: hasPtlError || isPtlPending ? 600 : 500,
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Target MGRS"
              name="mgrs_target"
              value={form.mgrs_target}
              onChange={handleChange}
              size="small"
              error={!!mgrsError.target}
              helperText={mgrsError.target || "Ex.: 29SND6000050000"}
              onBlur={() => handleMgrsConfirm("target")}
              sx={{
                ...compactFieldSx,
                "& .MuiFormHelperText-root": { fontSize: 11, margin: "2px 0 0 0" },
              }}
            />
          </Grid>

          <Grid item xs={12} md={1}>
            <TextField
              fullWidth
              label="Alt (m)"
              name="target_altitude"
              type="number"
              value={form.target_altitude}
              onChange={handleChange}
              size="small"
              sx={compactFieldSx}
            />
          </Grid>
        </Grid>

        {/* Save closes the panel. Validation: PTL must be filled & valid. */}
        {onSave && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.25 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<CheckRoundedIcon fontSize="small" />}
              disabled={isPtlPending || hasPtlError}
              onClick={() => {
                if (typeof validatePtl === "function" && !validatePtl()) return;
                onSave();
              }}
              sx={(theme) => ({
                ...tacticalActionSx(theme, "accent", "contained"),
                minHeight: 36,
                fontWeight: 700,
                minWidth: 120,
              })}
            >
              Save
            </Button>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
